<template>
  <div class="example-square">
    <!-- 示例列表 -->
    <div class="example-list">
      <div v-for="(example, index) in filteredExamples" :key="index" class="example-item">
        <div class="example-header">
          <div class="user-info">
            <div class="avatar">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L15 8L21 9L17 14L18 20L12 17L6 20L7 14L3 9L9 8L12 2Z" fill="#ffd700"/>
              </svg>
            </div>
            <div class="user-details">
              <span class="user-name">{{ example.userName }}</span>
              <span class="user-tag">AI 生成</span>
            </div>
          </div>
        </div>

        <div class="example-content">
          <div :class="['prompt-text', { 'full-width': !hasOriginalImages(example) }]">
            <span v-if="hasOriginalImages(example)" class="prompt-label">图 {{ index + 1 }}</span>
            {{ example.prompt }}
          </div>

          <div :class="['images-container', { single: !hasOriginalImages(example) }]">
            <!-- 显示所有原图 -->
            <template v-if="hasOriginalImages(example)">
              <div 
                v-for="(originalImg, imgIndex) in getOriginalImages(example)" 
                :key="'original-' + imgIndex"
                class="image-wrapper original-image"
                @click="openImagePreview(originalImg)"
              >
                <img :src="originalImg" :alt="'原图' + (imgIndex + 1)" />
                <div class="image-label">原图{{ imgIndex + 1 }}</div>
                <div class="zoom-icon">
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="11" cy="11" r="8" stroke="currentColor" stroke-width="2"/>
                    <path d="M21 21L16.65 16.65" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                    <path d="M11 8V14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                    <path d="M8 11H14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  </svg>
                </div>
              </div>
            </template>
            
            <!-- 显示生成结果 -->
            <div class="image-wrapper" @click="openImagePreview(example.generatedImage)">
              <img :src="example.generatedImage" alt="生成结果" />
              <div v-if="!hasOriginalImages(example)" class="image-label generated">AI 生成</div>
              <div class="zoom-icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="11" cy="11" r="8" stroke="currentColor" stroke-width="2"/>
                  <path d="M21 21L16.65 16.65" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  <path d="M11 8V14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                  <path d="M8 11H14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                </svg>
              </div>
            </div>
          </div>

          <div class="example-actions">
            <button class="action-btn copy-style" @click="() => { console.log('按钮被点击', example); copyStyle(example); }">
              <svg class="btn-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2"/>
                <circle cx="12" cy="12" r="6" stroke="currentColor" stroke-width="2"/>
                <circle cx="12" cy="12" r="2" fill="currentColor"/>
              </svg>
              制同款
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 图片预览弹窗 -->
    <div v-if="previewImage" class="image-preview-overlay" @click="closeImagePreview">
      <div class="image-preview-container" @click.stop>
        <img :src="previewImage" alt="预览图片" />
        <button class="close-preview-btn" @click="closeImagePreview">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ExampleSquare',
  props: {
    filterType: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      currentToolType: '', // 当前选中的工具类型
      previewImage: null, // 预览图片地址
      examples: [] // 从 JSON 文件加载示例数据
    };
  },
  computed: {
    filteredExamples() {
      // 优先使用 prop 传入的 filterType，否则使用内部状态
      const filter = this.filterType || this.currentToolType;
      // 如果没有选择工具类型，显示所有示例
      if (!filter) {
        return this.examples;
      }
      // 否则只显示与当前工具类型匹配的示例
      return this.examples.filter(example => example.toolType === filter);
    }
  },
  mounted() {
    this.loadFromQuery();
    this.loadExamples();
  },
  watch: {
    filterType(newVal) {
      console.log('FilterType changed:', newVal);
    }
  },
  methods: {
    loadFromQuery() {
      const query = this.$route.query;
      if (query.toolType) {
        this.currentToolType = query.toolType;
        console.log('当前工具类型:', this.currentToolType);
      }
    },
    async loadExamples() {
      try {
        const response = await fetch('/examples.json');
        if (response.ok) {
          this.examples = await response.json();
          console.log('示例数据加载成功:', this.examples);
        } else {
          console.error('加载示例数据失败:', response.status);
        }
      } catch (error) {
        console.error('加载示例数据出错:', error);
      }
    },
    // 检查是否有原图（支持单个路径或多个路径）
    hasOriginalImages(example) {
      return example.originalImage && example.originalImage.trim() !== '';
    },
    // 获取所有原图路径数组（支持逗号分隔的多个路径）
    getOriginalImages(example) {
      if (!example.originalImage || !example.originalImage.trim()) {
        return [];
      }
      // 按英文逗号分隔，去除空白
      return example.originalImage.split(',').map(path => path.trim()).filter(path => path);
    },
    continueEdit(example) {
      console.log('继续编辑', example);
      // 获取所有原图
      const originalImages = this.getOriginalImages(example);
      
      this.$router.push({
        path: '/image-edit',
        query: {
          type: 'continue',
          prompt: example.prompt,
          image: example.generatedImage,
          originalImages: originalImages.length > 0 ? originalImages.join(',') : '',
          hasOriginal: this.hasOriginalImages(example)
        }
      });
    },
    copyStyle(example) {
      console.log('制同款', example);
      console.log('originalImage:', example.originalImage);
      console.log('generatedImage:', example.generatedImage);
      
      // 获取所有原图
      const originalImages = this.getOriginalImages(example);
      
      console.log('所有原图:', originalImages);
      // 使用继续编辑的功能：传递生成图和所有原图
      this.$router.push({
        path: '/image-edit',
        query: {
          type: 'continue',
          prompt: example.prompt,
          image: example.generatedImage,
          originalImages: originalImages.length > 0 ? originalImages.join(',') : '',
          hasOriginal: this.hasOriginalImages(example),
          model: example.model || 'nano'
        }
      });
    },
    openImagePreview(imageUrl) {
      this.previewImage = imageUrl;
      document.body.style.overflow = 'hidden';
    },
    closeImagePreview() {
      this.previewImage = null;
      document.body.style.overflow = '';
    }
  }
};
</script>

<style scoped>
.example-square {
  min-height: 100vh;
  background: #faf8f5;
  padding: 20px;
}

.example-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.example-item {
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.example-header {
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.avatar {
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar svg {
  width: 20px;
  height: 20px;
}

.user-details {
  display: flex;
  align-items: center;
  gap: 8px;
}

.user-name {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.user-tag {
  font-size: 11px;
  color: #999;
  background: #f5f5f5;
  padding: 2px 8px;
  border-radius: 10px;
}

.example-content {
  padding: 20px;
}

.prompt-text {
  font-size: 14px;
  line-height: 1.6;
  color: #333;
  margin-bottom: 16px;
  padding: 12px;
  background: #fafafa;
  border-radius: 8px;
}

.prompt-text.full-width {
  background: none;
  padding: 0;
}

.prompt-label {
  display: inline-block;
  background: #333;
  color: white;
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 4px;
  margin-right: 8px;
}

.images-container {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  align-items: stretch;
  flex-wrap: wrap;
}

.images-container.single .image-wrapper {
  flex: 1;
  min-width: 200px;
}

/* 多张原图时，每张图片自适应宽度 */
.images-container:not(.single) .image-wrapper {
  flex: 1;
  min-width: 150px;
}

.image-wrapper.original-image {
  border: 2px solid #e0e0e0;
}

.image-wrapper {
  position: relative;
  flex: 1;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  min-height: 200px;
  max-height: 400px;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
}

.image-wrapper img {
  max-width: 100%;
  max-height: 400px;
  width: auto;
  height: auto;
  object-fit: contain;
  display: block;
}

.image-label {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  font-size: 11px;
  padding: 3px 8px;
  border-radius: 4px;
}

.image-label.generated {
  background: rgba(255, 149, 0, 0.9);
}

/* 放大图标 */
.zoom-icon {
  position: absolute;
  bottom: 8px;
  right: 8px;
  width: 32px;
  height: 32px;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  cursor: pointer;
}

.zoom-icon svg {
  width: 18px;
  height: 18px;
  color: white;
}

.image-wrapper:hover .zoom-icon {
  opacity: 1;
}

.image-wrapper {
  cursor: pointer;
}

/* 图片预览弹窗 */
.image-preview-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.image-preview-container {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
}

.image-preview-container img {
  max-width: 100%;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 8px;
}

.close-preview-btn {
  position: absolute;
  top: -40px;
  right: 0;
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.3s ease;
}

.close-preview-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

.close-preview-btn svg {
  width: 20px;
  height: 20px;
  color: white;
}

.example-actions {
  display: flex;
  gap: 10px;
}

.action-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 12px;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.action-btn.continue-edit {
  background: linear-gradient(135deg, #ff9500 0%, #ffb347 100%);
  color: white;
  border: none;
}

.action-btn.copy-style {
  background: white;
  color: #ff9500;
  border: 1px solid #ff9500;
}

.action-btn:active {
  transform: scale(0.98);
}

.btn-icon {
  width: 16px;
  height: 16px;
  stroke: currentColor;
}
</style>
